import {
	createVestingTerm,
	deleteVestingTerm,
	updateVestingTerm,
	UpdateVestingTermArgs,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	createOrganizationVestingTerm,
	deleteOrganizationVestingTerm,
	updateOrganizationVestingTerm,
} from '@dealstack/frontend/shared/dataExchange/services/programme-defaults-vesting.service';
import {
	CreateVestingTermDto,
	UpdateVestingTermDto,
	VestingMode,
	VestingTermRes,
	VestingTermTimeUnitType,
	VestingTermType,
} from '@dealstack/shared/lib/generated';
import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';

const DEFAULT_VESTING_TERM: CreateVestingTermDto = {
	name: 'New vesting term',
	type: VestingTermType.GRADED,
	timeBasedPercentage: 100,
	performancePercentage: 0,
	vestingMode: VestingMode.VALUE,
	optionPeriod: 12,
	optionPeriodTimeUnit: VestingTermTimeUnitType.MONTHLY,
};

interface UseVestingTermMutationsArgs {
	vestingTerms: VestingTermRes[] | undefined;
	currentVestingTermId: string;
	setCurrentVestingTermId: (id: string) => void;
	refetchVestingTerms: () => Promise<unknown>;
	organizationId?: string;
}

export const useVestingTermMutations = ({
	vestingTerms,
	currentVestingTermId,
	setCurrentVestingTermId,
	refetchVestingTerms,
	organizationId,
}: UseVestingTermMutationsArgs) => {
	const tCommonUi = useTranslationCommonUi();

	const createVestingTermRequest = useMutation({
		mutationFn: (args: CreateVestingTermDto) =>
			organizationId
				? createOrganizationVestingTerm(organizationId, args)
				: createVestingTerm(args),
	});
	const updateVestingTermRequest = useMutation({
		mutationFn: (args: UpdateVestingTermArgs) =>
			organizationId
				? updateOrganizationVestingTerm(organizationId, args)
				: updateVestingTerm(args),
	});
	const deleteVestingTermRequest = useMutation({
		mutationFn: (args: { id: string }) =>
			organizationId
				? deleteOrganizationVestingTerm(organizationId, args)
				: deleteVestingTerm(args),
	});

	const getNewVestingTermName = useCallback(() => {
		const baseName = tCommonUi('vesting_terms.new_vesting_term');
		const newVestingTerms = vestingTerms?.filter((vestingTerm) =>
			vestingTerm.name.startsWith(baseName)
		);

		if (isEmpty(newVestingTerms)) {
			return baseName;
		}

		const numberedSuffix = /\((\d+)\)$/;
		const newVestingTermsNumbers = newVestingTerms?.map((vestingTerm) => {
			const match = vestingTerm.name.match(numberedSuffix);
			return match ? parseInt(match[1], 10) : 0;
		});

		return `${baseName} (${Math.max(...(newVestingTermsNumbers ?? [0])) + 1})`;
	}, [vestingTerms, tCommonUi]);

	const onUpdateVestingTerm = useCallback(
		(id: string, formValues: UpdateVestingTermDto) => {
			updateVestingTermRequest.mutate(
				{ id, ...formValues },
				{
					onSuccess: async () => {
						await refetchVestingTerms();
					},
				}
			);
		},
		[updateVestingTermRequest, refetchVestingTerms]
	);

	const onCreateVestingTerm = useCallback(() => {
		createVestingTermRequest.mutate(
			{ ...DEFAULT_VESTING_TERM, name: getNewVestingTermName() },
			{
				onSuccess: async (data) => {
					await refetchVestingTerms();

					if (data.payload) {
						setCurrentVestingTermId(data.payload.id);
					}
				},
			}
		);
	}, [
		createVestingTermRequest,
		refetchVestingTerms,
		setCurrentVestingTermId,
		getNewVestingTermName,
	]);

	const onDeleteVestingTerm = useCallback(
		(id: string) => {
			deleteVestingTermRequest.mutate(
				{ id },
				{
					onSuccess: async () => {
						if (id === currentVestingTermId) {
							const remaining = vestingTerms?.filter((term) => term.id !== id) ?? [];
							const lastVestingTermId = remaining[remaining.length - 1]?.id ?? '';

							setCurrentVestingTermId(lastVestingTermId);
						}

						await refetchVestingTerms();
					},
				}
			);
		},
		[
			deleteVestingTermRequest,
			refetchVestingTerms,
			vestingTerms,
			currentVestingTermId,
			setCurrentVestingTermId,
		]
	);

	const onTabNameChange = useCallback(
		(id: string, name: string) => {
			updateVestingTermRequest.mutate(
				{ id, name },
				{
					onSuccess: async () => {
						await refetchVestingTerms();
					},
				}
			);
		},
		[updateVestingTermRequest, refetchVestingTerms]
	);

	const deleteLoading = deleteVestingTermRequest.isPending;

	return useMemo(
		() => ({
			deleteLoading,
			onCreateVestingTerm,
			onUpdateVestingTerm,
			onDeleteVestingTerm,
			onTabNameChange,
		}),
		[
			deleteLoading,
			onCreateVestingTerm,
			onUpdateVestingTerm,
			onDeleteVestingTerm,
			onTabNameChange,
		]
	);
};
